import type { ChartSeries, TimePoint } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { formatBucket } from './format';
import type { Grain } from './filters';

type SeriesTableProps = {
  data: TimePoint[];
  series: ChartSeries[];
  grain: Grain;
  valueFormat?: (n: number) => string;
};

/** The table twin of a time-series chart: one row per bucket, one column per series. */
export function SeriesTable({ data, series, grain, valueFormat }: SeriesTableProps) {
  const { t, i18n } = useTranslation('admin');
  const fmt = valueFormat ?? ((n: number) => String(n));
  return (
    <div className="max-h-80 overflow-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-muted/60">
          <tr className="border-b">
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              {t('analytics.common.period')}
            </th>
            {series.map((s) => (
              <th key={s.key} className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.bucket} className="border-b last:border-0">
              <td className="px-3 py-1.5">{formatBucket(point.bucket, grain, i18n.language)}</td>
              {series.map((s) => {
                const raw = point[s.key];
                const value = typeof raw === 'number' ? raw : Number(raw ?? 0);
                return (
                  <td key={s.key} className="px-3 py-1.5 text-right tabular-nums">
                    {Number.isFinite(value) ? fmt(value) : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
